import {useCallback, useRef, type TouchEvent} from "react";

const SWIPE_MIN_HORIZONTAL_PX = 48;

type TouchPoint = {
    x: number;
    y: number;
};

/**
 * Horizontal swipe on the calendar body: swipe left -> next period, swipe right -> previous.
 * Ignores gestures that are primarily vertical so time-grid / list scrolling still works.
 */
export function useCalendarSwipeNavigation(args: {
    enabled: boolean;
    onPrev: () => void;
    onNext: () => void;
}): {
    onTouchStart: (event: TouchEvent<HTMLElement>) => void;
    onTouchEnd: (event: TouchEvent<HTMLElement>) => void;
    onTouchCancel: () => void;
} {
    const touchStartRef = useRef<TouchPoint | null>(null);

    const onTouchStart = useCallback(
        (event: TouchEvent<HTMLElement>) => {
            if (args.enabled !== true || event.touches.length !== 1) {
                return;
            }
            const touch = event.touches[0];
            if (touch === undefined) {
                return;
            }
            touchStartRef.current = {
                x: touch.clientX,
                y: touch.clientY,
            };
        },
        [args.enabled],
    );

    const onTouchEnd = useCallback(
        (event: TouchEvent<HTMLElement>) => {
            if (args.enabled !== true) {
                return;
            }
            const start = touchStartRef.current;
            touchStartRef.current = null;
            if (start === null) {
                return;
            }
            const touch = event.changedTouches[0];
            if (touch === undefined) {
                return;
            }
            const deltaX = touch.clientX - start.x;
            const deltaY = touch.clientY - start.y;
            if (Math.abs(deltaX) < SWIPE_MIN_HORIZONTAL_PX) {
                return;
            }
            if (Math.abs(deltaX) <= Math.abs(deltaY)) {
                return;
            }
            if (deltaX < 0) {
                args.onNext();
                return;
            }
            args.onPrev();
        },
        [args.enabled, args.onNext, args.onPrev],
    );

    const onTouchCancel = useCallback(() => {
        touchStartRef.current = null;
    }, []);

    return {
        onTouchStart: onTouchStart,
        onTouchEnd: onTouchEnd,
        onTouchCancel: onTouchCancel,
    };
}
